"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.promoteToIdea = promoteToIdea;
const path = __importStar(require("path"));
const dist_1 = require("../../core/dist");
const aiSummarization_1 = require("./utils/aiSummarization");
const resolveThreadFolder_1 = require("./utils/resolveThreadFolder");
const SYSTEM_PROMPT = `You are an AI assistant embedded in REslava Loom, a document-driven workflow system.
Your task: read the chat conversation and extract the most important idea discussed.
Respond with exactly this format — nothing else before or after:

TITLE: <one concise line describing the idea>

<idea body in Markdown with these sections>
## Problem
<what pain or gap this idea addresses>

## Idea
<the core concept in 2-3 sentences>

## Why now
<what makes this worth pursuing>

## Open questions
<what needs to be answered before committing to a design>

## Next step
<design | spike | discard>`;
async function promoteToIdea(input, deps) {
    const doc = await deps.loadDoc(input.filePath);
    // Resolve the target: an explicit targetThreadUlid is a stable th_ ULID → folder
    // (never fabricates); a derived location already yields the folder slug.
    let weaveId;
    let threadId;
    if (input.targetWeaveSlug) {
        weaveId = input.targetWeaveSlug;
        threadId = input.targetThreadUlid
            ? (await (0, resolveThreadFolder_1.resolveThreadFolder)(input.targetWeaveSlug, input.targetThreadUlid, {
                getActiveLoomRoot: () => deps.loomRoot, loadDoc: deps.loadDoc, fs: deps.fs,
            })).threadSlug
            : undefined;
    }
    else {
        ({ weaveId, threadId } = deriveLocation(input.filePath, deps.loomRoot));
    }
    let title;
    let body;
    if (input.body !== undefined) {
        body = input.body;
        title = input.title ?? doc.title;
    }
    else {
        if (!doc.content || doc.content.trim().length === 0) {
            throw new Error('Chat document is empty.');
        }
        const messages = (0, aiSummarization_1.buildSummarizationMessages)(SYSTEM_PROMPT, 'chat conversation', doc.content);
        const reply = await deps.aiClient.complete(messages);
        ({ title, body } = (0, aiSummarization_1.parseTitleAndBody)(reply));
    }
    const targetDir = threadId
        ? path.join(deps.loomRoot, 'loom', weaveId, threadId)
        : path.join(deps.loomRoot, 'loom', weaveId);
    await deps.fs.ensureDir(targetDir);
    let ideaFilename;
    let filePath;
    if (threadId) {
        // Thread-level: canonical filename is the flat singleton idea.md (one per thread).
        filePath = path.join(targetDir, (0, dist_1.singletonFileName)('idea'));
        // Dual-read the singleton guard: refuse if either the flat or legacy idea exists.
        const legacyIdea = path.join(targetDir, `${threadId}-idea.md`);
        if ((await deps.fs.pathExists(filePath)) || (await deps.fs.pathExists(legacyIdea))) {
            throw new Error(`Thread '${threadId}' already has an idea. Refine the existing one instead.`);
        }
    }
    else {
        // Weave-root doc: use kebab-of-title to allow multiple
        const existingFiles = await deps.fs.readdir(targetDir).catch(() => []);
        const ideaFiles = existingFiles.filter(f => f.endsWith('-idea.md'));
        ideaFilename = generateIdeaId(title, weaveId, ideaFiles);
        filePath = path.join(targetDir, `${ideaFilename}.md`);
    }
    const idScope = threadId ?? weaveId;
    const ideaId = (0, dist_1.generateDocId)('idea');
    const frontmatter = (0, dist_1.createBaseFrontmatter)('idea', ideaId, title, idScope);
    const ideaDoc = {
        ...frontmatter,
        type: 'idea',
        status: 'draft',
        content: input.body !== undefined ? body : `# ${title}\n\n${body}`,
    };
    await deps.saveDoc(ideaDoc, filePath);
    return { filePath, title };
}
function deriveLocation(filePath, loomRoot) {
    const rel = path.relative(path.join(loomRoot, 'loom'), filePath);
    const parts = rel.split(/[\\/]/);
    if (parts.length < 2)
        throw new Error(`Cannot derive weave from path: ${rel}`);
    const weaveId = parts[0];
    // loom/{weave}/chats/file → weave-level chat (no thread)
    if (parts.length >= 3 && parts[1] === 'chats')
        return { weaveId };
    // loom/{weave}/{thread}/chats/file or loom/{weave}/{thread}/{anything}
    if (parts.length >= 3)
        return { weaveId, threadId: parts[1] };
    // loom/{weave}/file (weave-root doc)
    return { weaveId };
}
function generateIdeaId(title, weaveId, existingFiles) {
    const kebab = title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
    const base = `${weaveId}-${kebab}-idea`;
    const taken = new Set(existingFiles.map(f => f.replace(/\.md$/, '')));
    if (!taken.has(base))
        return base;
    let n = 2;
    while (taken.has(`${base}-${n}`))
        n++;
    return `${base}-${n}`;
}
